/**
 * Every vehicle team in the database, loaded once for the whole shell.
 *
 * This is what turns a convoy into an *effective status*: the rosters and the
 * dashboard all need to know which vehicles and which soldiers are out right
 * now, and that answer lives in the teams rather than on the records
 * themselves. Loading it here — beside the fleet and the board — means the four
 * tabs read one list and can never disagree about who is on a mission.
 *
 * It re-reads itself off the global reactive store, so a convoy saved on the
 * tasks board updates the vehicle roster and the soldier roster without either
 * screen being touched. Reads go through `src/db/assignment-store.ts`; nothing
 * here talks to Dexie directly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllTaskTeams } from '../../db/assignment-store';
import { activeAssignmentIndex } from '../../core/effective-status';
import type { AssignmentIndex } from '../../core/effective-status';
import { useStoreEvents } from '../../hooks/use-store-events';
import type { TaskTeam } from '../../types/assignment';
import type { Task } from '../../types/task';

export interface AssignmentsData {
  teams: TaskTeam[];
  /** Who is out right now, derived from the teams and the board together. */
  index: AssignmentIndex;
  loading: boolean;
  /** Re-reads every team — call it after a convoy is saved or dropped. */
  reload: () => Promise<void>;
}

export function useAssignments(tasks: readonly Task[]): AssignmentsData {
  const [teams, setTeams] = useState<TaskTeam[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setTeams(await getAllTaskTeams());
  }, []);

  useEffect(() => {
    void (async () => {
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  // A team is written by the assignment store and released by the task store —
  // closing a task hands its crew back without a team row being touched — so
  // both domains have to be heard.
  useStoreEvents(reload, ['assignments', 'tasks']);

  // Deliberately not narrowed to today: a convoy that left yesterday and has
  // not been closed is still out, and the roster has to say so.
  const index = useMemo(() => activeAssignmentIndex(teams, tasks), [teams, tasks]);

  return { teams, index, loading, reload };
}
